import type { CommunityMember } from "@circle-react/types";
import { MultiSelect } from "@circleco/compass/components/MultiSelect";
import type { SelectOption } from "@circleco/compass/components/MultiSelect";
import type { UseAsyncSearchReturn } from "@circleco/compass/hooks/useAsyncSearch";
import { compact, keyBy } from "lodash";
import { useCallback, useMemo, useState } from "react";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

import { isValidEmail } from "./constants";
import { useCommunityMemberHydration } from "./useCommunityMemberHydration";
import {
  type CommunityMemberOption,
  type SelectedCommunityMember,
  toSelectedCommunityMember,
  useCommunityMemberSelectOptions,
} from "./useCommunityMemberSelectOptions";

type MemberOrEmailOption = SelectOption<number | string>;

const isCommunityMemberOption = (
  option: MemberOrEmailOption,
): option is CommunityMemberOption => "member" in option;

const I18N_ROOT = "community_member_select";

interface MultiMemberSelectProps {
  excludeAdmins?: boolean;
  filters?: Record<string, unknown>;
  getCommunityMemberText?: (member: CommunityMember) => string;
  includeInactive?: boolean;
  onChange?: (values: (number | string)[]) => void;
  onSelectMember?: (member: SelectedCommunityMember) => void;
  searchProps: UseAsyncSearchReturn<CommunityMemberOption>["searchProps"];
  selectProps: Record<string, unknown>;
  sharedProps: Record<string, unknown>;
  value: (number | string)[];
  valueField?: "id" | "user_id";
}

export const MultiMemberSelect = ({
  sharedProps,
  selectProps,
  searchProps,
  onChange,
  onSelectMember,
  value,
  excludeAdmins = false,
  filters,
  getCommunityMemberText,
  includeInactive = false,
  valueField = "id",
}: MultiMemberSelectProps) => {
  const toast = useToast();

  const { hydratedMembers, isHydrationLoading } = useCommunityMemberHydration({
    excludeAdmins,
    filters,
    ids: value,
    includeInactive,
    valueField,
  });

  const { options: hydratedOptions } = useCommunityMemberSelectOptions({
    communityMembers: hydratedMembers,
    getCommunityMemberText,
    valueField,
  });

  // Cache option data at selection time so chips survive the window between
  // searchProps.options being cleared (popup close) and hydration refetching
  // the newly-selected member. Updated synchronously in handleValueChange so
  // the cache is part of the same React batch as the value update — a useEffect
  // would be too late when React 18 batches both the value change and the
  // options clear into a single render.
  const [selectionCache, setSelectionCache] = useState<
    Record<string | number, MemberOrEmailOption>
  >({});

  const selectedOptions = useMemo(() => {
    const optionsByValue = keyBy(
      [...hydratedOptions, ...searchProps.options],
      "value",
    );

    return compact(
      value.map(v => {
        const knownOption = optionsByValue[v] ?? selectionCache[v];
        if (knownOption) return knownOption;

        if (typeof v === "string" && isValidEmail(v)) {
          return { value: v, label: v };
        }

        return null;
      }),
    );
  }, [value, hydratedOptions, searchProps.options, selectionCache]);

  const mergedOptions = useMemo(() => {
    const selectedValues = new Set(value);
    return [
      ...selectedOptions,
      ...searchProps.options.filter(o => !selectedValues.has(o.value)),
    ];
  }, [selectedOptions, searchProps.options, value]);

  const handleValueChange = useCallback(
    (opts: MemberOrEmailOption[]) => {
      // Snapshot option data at selection time so chips persist after the popup
      // closes and searchProps.options is cleared.
      const newCache: Record<string | number, MemberOrEmailOption> = {};
      opts.forEach(o => {
        newCache[o.value] = o;
        if (!value.includes(o.value) && isCommunityMemberOption(o)) {
          onSelectMember?.(toSelectedCommunityMember(o));
        }
      });
      setSelectionCache(prev => ({ ...prev, ...newCache }));

      const incoming = opts.map(o => o.value);
      if (isHydrationLoading) {
        // While the initial hydration query is in-flight, base-ui sees an empty
        // controlled selection (unresolved IDs aren't yet in optionsByValue).
        // Without this guard, clicking the first new option calls onChange with
        // only that option, silently dropping any previously-assigned IDs.
        const incomingSet = new Set(incoming);
        const droppedIds = value.filter(v => !incomingSet.has(v));
        onChange?.([...incoming, ...droppedIds]);
      } else {
        onChange?.(incoming);
      }
    },
    [onChange, onSelectMember, isHydrationLoading, value],
  );

  const handleCreateOption = useCallback(
    (inputValue: string) => {
      if (!isValidEmail(inputValue)) {
        toast.error(t([I18N_ROOT, "invalid_email"]));
        return;
      }
      onSelectMember?.({
        id: inputValue,
        name: inputValue,
        email: inputValue,
        avatarUrl: "",
      });
      onChange?.([...value, inputValue]);
    },
    [onChange, onSelectMember, value, toast],
  );

  return (
    <MultiSelect
      {...sharedProps}
      {...selectProps}
      {...searchProps}
      options={mergedOptions}
      value={selectedOptions}
      onValueChange={handleValueChange}
      onCreateOption={handleCreateOption}
    />
  );
};
